import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import { z } from "zod";

export type ProposalReviewerJSON = z.infer<typeof ProposalReviewerSchema.ProposalReviewerJSONZ>;
export type ProposalReviewerParams = z.infer<typeof ProposalReviewerSchema.ProposalReviewerZ>;

export default class ProposalReviewerSchema {
  public static readonly ProposalReviewerJSONZ = z.object({
    userId: z.uuid(),
    legalEntityId: z.uuid(),
    canEdit: z.boolean(),
    dateApproved: z.iso.datetime({ offset: true }).nullish(),
    customMessage: z.string().nullish(),
  });

  public static readonly ProposalReviewerZ = z
    .object({
      entityMember: EntityMemberSchema.EntityMemberZ,
      canEdit: z.boolean(),
      dateApproved: z.instanceof(AHDate).optional(),
      customMessage: z.string().optional(),
    })
    .readonly();

  public static validate(values: unknown): ProposalReviewerParams {
    return parseOrThrow(ProposalReviewerSchema.ProposalReviewerZ, values, {
      logError: true,
      schemaName: "ProposalReviewer",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): ProposalReviewerJSON {
    return parseOrThrow(ProposalReviewerSchema.ProposalReviewerJSONZ, json, {
      logError: true,
      schemaName: "ProposalReviewer",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is ProposalReviewerParams {
    try {
      ProposalReviewerSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is ProposalReviewerJSON {
    try {
      ProposalReviewerSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: ProposalReviewerJSON): ProposalReviewerParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(ProposalReviewerSchema.ProposalReviewerJSONZ, json, {
      logError: true,
      schemaName: "ProposalReviewer",
      operation: "deserialize",
      payloadSample: json,
    });
    return ProposalReviewerSchema.codec.decode(parsed);
  }

  public static serialize(params: ProposalReviewerParams): ProposalReviewerJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = ProposalReviewerSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return ProposalReviewerSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    ProposalReviewerSchema.ProposalReviewerJSONZ,
    ProposalReviewerSchema.ProposalReviewerZ,
    {
      decode: (data) => ({
        entityMember: EntityMemberSchema.deserialize(data),
        canEdit: data.canEdit,
        dateApproved: AHDate.fromJSON(data.dateApproved) ?? undefined,
        customMessage: data.customMessage ?? undefined,
      }),
      encode: (reviewer) => ({
        userId: reviewer.entityMember.userId.toJSON(),
        legalEntityId: reviewer.entityMember.legalEntityId.toJSON(),
        canEdit: reviewer.canEdit,
        dateApproved: reviewer.dateApproved?.toJSON(),
        customMessage: reviewer.customMessage,
      }),
    },
  );
}
